import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Tabs,
  Text,
  VStack,
  type TabsValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useContactSearch, useContactSearchOptions } from '../../../hooks/useContactSearch'
import { useAddAttendees } from '../../../hooks/useLobbyDay'
import { usePriorEventAttendees, usePriorEvents } from '../../../hooks/useLobbyRecruit'
import { flagDuplicateNames } from '../pickerDuplicates'
import { RecruitResultRow } from './RecruitResultRow'
import { EmployerFilter, type EmployerSelection } from './EmployerFilter'
import { SectorFilter } from '../../contacts/SectorFilter'
import { TagBucketFilter, type TagBucketValue } from '../../contacts/TagBucketFilter'
import { DistrictFilter, EMPTY_DISTRICT_FILTER, type DistrictFilterValue } from '../../contacts/DistrictFilter'
import { ContactDetailModal } from '../../contactDetail/ContactDetailModal'
import { ContactCreateModal } from '../../contacts/ContactCreateModal'
import type { ContactSearchRequest, ContactSearchResult } from '../../../types/contact'
import type { AddedAttendeeToken, Chamber } from '../../../types/lobbyDay'

const EMPTY_TAG_BUCKETS: TagBucketValue = { includeTagIds: [], excludeTagIds: [] }

// A quiet uppercase section header, grouping the Search-tab filters into a scan order
// (Name & contact / Workplace / Districts / Tags). Sits one level above the filters' own
// sub-labels (e.g. "Employer sector", "Must have all of these"), so no double-labeling.
function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <VStack align="stretch" gap={2}>
      <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase" color="muted">
        {label}
      </Text>
      {children}
    </VStack>
  )
}

type Mode = 'search' | 'reinvite'

const ANY_PRIOR_OPTION = 'any'

const CHAMBER_TO_DISTRICT_KEY: Record<Chamber, keyof DistrictFilterValue | null> = {
  StateHouse: 'stateHouseDistricts',
  StateSenate: 'stateSenateDistricts',
  USHouse: 'congressionalDistricts',
  USSenate: null, // whole-state — no district chip
}

interface Props {
  eventId: string
  findConstituents?: { nonce: number; chamber: Chamber; districtValues: string[] } | undefined
  existingContactIds: string[]
  // LW-24: lets a just-added attendee's show-once link land straight in RosterView's Copy link
  // state instead of Rotate link. Optional -- callers that don't care about the show-once tokens
  // (e.g. tests) can omit it.
  onAttendeesAdded?: (tokens: AddedAttendeeToken[]) => void
}

export function RecruitWorkspace({ eventId, findConstituents, existingContactIds, onAttendeesAdded }: Props) {
  const [mode, setMode] = useState<Mode>('search')
  const [addingId, setAddingId] = useState<string | null>(null)
  const [openContactId, setOpenContactId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const addAttendees = useAddAttendees(eventId)
  const existingSet = useMemo(() => new Set(existingContactIds), [existingContactIds])

  const add = async (contactId: string) => {
    setAddingId(contactId)
    try {
      const result = await addAttendees.mutateAsync([contactId])
      toaster.create({ type: 'success', title: 'Attendee added' })
      onAttendeesAdded?.(result.tokens)
    } finally { setAddingId(null) }
  }

  // ----- Search mode state -----
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', streetAddress: '', city: '', zip: '' })
  // Union number rides its own state: the request field is `unionMemberNumber`, which doesn't
  // match a `form` key, so it can't ride the form key-spread below.
  const [unionNumber, setUnionNumber] = useState('')
  const [unionOnly, setUnionOnly] = useState(true)
  const [searchEmployer, setSearchEmployer] = useState<EmployerSelection>({})
  const [searchSectors, setSearchSectors] = useState<string[]>([])
  const [searchDistricts, setSearchDistricts] = useState<DistrictFilterValue>(EMPTY_DISTRICT_FILTER)
  const [searchTags, setSearchTags] = useState<TagBucketValue>(EMPTY_TAG_BUCKETS)
  const { data: searchOptions } = useContactSearchOptions()
  const [submitted, setSubmitted] = useState<ContactSearchRequest | null>(null)
  const [emptyMatchNote, setEmptyMatchNote] = useState(false)
  const search = useContactSearch(submitted ?? {}, !!submitted && mode === 'search')

  // Build the submitted request from an explicit DistrictFilterValue (used by both the manual
  // Search button and the Find-constituents prefill, so the two can't drift).
  const buildRequest = (districts: DistrictFilterValue): ContactSearchRequest => ({
    ...Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim())),
    // Strip a leading `#`: the contact-detail modal displays the number as `#M61703`, so a copy-paste
    // naturally carries the `#`, which the stored value doesn't have (B3). Backend does a substring
    // match, so trimming the decorative prefix is all that's needed.
    unionMemberNumber: unionNumber.replace(/^[#\s]+/, '').trim() || undefined,
    ...searchEmployer,
    employerTypes: searchSectors.length > 0 ? searchSectors : undefined,
    stateHouseDistricts: districts.stateHouseDistricts.length > 0 ? districts.stateHouseDistricts : undefined,
    stateSenateDistricts: districts.stateSenateDistricts.length > 0 ? districts.stateSenateDistricts : undefined,
    congressionalDistricts: districts.congressionalDistricts.length > 0 ? districts.congressionalDistricts : undefined,
    includeTagIds: searchTags.includeTagIds.length > 0 ? searchTags.includeTagIds : undefined,
    excludeTagIds: searchTags.excludeTagIds.length > 0 ? searchTags.excludeTagIds : undefined,
    unionMemberOnly: unionOnly,
    excludeLobbyDayEventId: eventId,
    useSmartNameSearch: true,
    pageSize: 25,
  })

  const runSearch = () => {
    const anyField = Object.values(form).some((v) => v.trim().length > 0)
    const anyUnionNumber = unionNumber.trim().length > 0
    const anyEmployer = !!searchEmployer.businessId || !!searchEmployer.employerName
    const anySector = searchSectors.length > 0
    const anyDistrict =
      searchDistricts.stateHouseDistricts.length > 0 ||
      searchDistricts.stateSenateDistricts.length > 0 ||
      searchDistricts.congressionalDistricts.length > 0
    const anyTags = searchTags.includeTagIds.length > 0 || searchTags.excludeTagIds.length > 0
    if (!anyField && !anyUnionNumber && !anyEmployer && !anySector && !anyDistrict && !anyTags) return
    setEmptyMatchNote(false)
    setSubmitted(buildRequest(searchDistricts))
  }

  // React to each Find-constituents click (nonce changes even when the row repeats).
  useEffect(() => {
    if (!findConstituents) return
    const key = CHAMBER_TO_DISTRICT_KEY[findConstituents.chamber]
    const values = findConstituents.districtValues
    if (!key || values.length === 0) {
      // No stored districts to seed — open Search cleanly with a note instead of a bare empty
      // result, and clear any stale prior search results out from under that note.
      setSearchDistricts(EMPTY_DISTRICT_FILTER)
      setEmptyMatchNote(true)
      setMode('search')
      setSubmitted(null)
      return
    }
    const districts: DistrictFilterValue = { ...EMPTY_DISTRICT_FILTER, [key]: values }
    setSearchDistricts(districts)
    setEmptyMatchNote(false)
    setMode('search')
    setSubmitted(buildRequest(districts))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findConstituents?.nonce])

  // ----- Re-invite mode state -----
  const [reinviteUnionOnly, setReinviteUnionOnly] = useState(true)
  const [priorSelection, setPriorSelection] = useState<string>('')
  const priorEvents = usePriorEvents(eventId)
  const specificPriorId = priorSelection && priorSelection !== ANY_PRIOR_OPTION ? priorSelection : undefined
  const priorAttendees = usePriorEventAttendees(eventId, specificPriorId, mode === 'reinvite')
  const anyPriorSearch = useContactSearch(
    { attendedPriorLobbyDay: true, excludeLobbyDayEventId: eventId, unionMemberOnly: reinviteUnionOnly, pageSize: 25 },
    mode === 'reinvite' && priorSelection === ANY_PRIOR_OPTION,
  )

  const renderResults = (rows: ContactSearchResult[], loading: boolean, empty: string) => {
    const dupes = flagDuplicateNames(rows)
    if (loading) return <VStack py={8}><Spinner size="lg" color="accent" /></VStack>
    if (rows.length === 0) return <Text color="gray.500" fontSize="sm" py={6} textAlign="center">{empty}</Text>
    return (
      <VStack align="stretch" gap={2}>
        {rows.map((c) => (
          <RecruitResultRow
            key={c.id} contact={c} isAttendee={existingSet.has(c.id)}
            isDuplicate={dupes.has(c.id)} adding={addingId === c.id} onAdd={add}
            onViewDetails={setOpenContactId}
          />
        ))}
      </VStack>
    );
  }

  return (
    <Box bg="surface" borderRadius="lg" shadow="md" mt={4}>
      <Tabs.Root
        value={mode}
        onValueChange={(details: TabsValueChangeDetails) => setMode(details.value as Mode)}
        colorPalette="brand"
      >
        <HStack justify="space-between" align="center" px={4} pt={2} flexWrap="wrap" gap={2}>
          <Tabs.List>
            <Tabs.Trigger value="search">Search</Tabs.Trigger>
            <Tabs.Trigger value="reinvite">Re-invite</Tabs.Trigger>
          </Tabs.List>
          <Button size="sm" variant="outline" colorPalette="brand" minH="44px" onClick={() => setCreateOpen(true)}>
            Add new contact
          </Button>
        </HStack>
      </Tabs.Root>

      <Box p={4}>
        {mode === 'search' && (
          <VStack align="stretch" gap={5}>
            <FieldGroup label="Name & contact">
              <SimpleGrid columns={{ base: 1, md: 4 }} gap={3}>
                <Input placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                <Input placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Input placeholder="Street address" value={form.streetAddress} onChange={(e) => setForm({ ...form, streetAddress: e.target.value })} gridColumn={{ md: 'span 2' }} />
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <Input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
              </SimpleGrid>
            </FieldGroup>

            <FieldGroup label="Workplace">
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} alignItems="start">
                <EmployerFilter value={searchEmployer} onChange={setSearchEmployer} />
                <Input placeholder="Union member ID" aria-label="Union member ID" value={unionNumber} onChange={(e) => setUnionNumber(e.target.value)} />
              </SimpleGrid>
              <SectorFilter
                options={searchOptions?.employerTypes ?? []}
                value={searchSectors}
                onChange={setSearchSectors}
              />
            </FieldGroup>

            <FieldGroup label="Districts">
              <DistrictFilter
                key={findConstituents?.nonce ?? 'manual'}
                label=""
                options={{
                  stateHouseDistricts: searchOptions?.stateHouseDistricts ?? [],
                  stateSenateDistricts: searchOptions?.stateSenateDistricts ?? [],
                  congressionalDistricts: searchOptions?.congressionalDistricts ?? [],
                }}
                value={searchDistricts}
                onChange={(next) => { setSearchDistricts(next); setEmptyMatchNote(false) }}
                activeChamber={findConstituents ? (CHAMBER_TO_DISTRICT_KEY[findConstituents.chamber] ?? undefined) : undefined}
              />
            </FieldGroup>

            <FieldGroup label="Tags">
              <TagBucketFilter value={searchTags} onChange={setSearchTags} />
            </FieldGroup>

            <HStack
              justify="space-between"
              align="center"
              gap={3}
              flexDir={{ base: 'column-reverse', md: 'row' }}
              alignItems={{ base: 'stretch', md: 'center' }}
              borderTopWidth="1px"
              borderColor="border"
              pt={4}
            >
              <Checkbox.Root minH="44px" onCheckedChange={(e) => setUnionOnly(e.checked === true)} checked={unionOnly}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Union members only</Checkbox.Label></Checkbox.Root>
              <Button colorPalette="brand" onClick={runSearch} w={{ base: 'full', md: 'auto' }} minH="44px">Search contacts</Button>
            </HStack>

            {emptyMatchNote && (
              <Text color="ink.2" fontSize="sm">
                No stored districts matched this legislator — refine filters manually.
              </Text>
            )}
            {submitted && renderResults(search.data?.items ?? [], search.isLoading, 'No contacts found.')}
          </VStack>
        )}

        {mode === 'reinvite' && (
          <VStack align="stretch" gap={4}>
            <HStack gap={4} flexWrap="wrap">
              <NativeSelect.Root>
                <NativeSelect.Field
                  placeholder="Choose a prior event…"
                  maxW={{ base: 'full', md: '320px' }}
                  value={priorSelection}
                  onChange={(e) => setPriorSelection(e.target.value)}>
                  <option value={ANY_PRIOR_OPTION}>Anyone with prior attendance</option>
                  {(priorEvents.data ?? []).map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name} ({ev.startDate}) — {ev.attendeeCount} attendees
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Checkbox.Root
                minH="44px"
                disabled={!!specificPriorId}
                onCheckedChange={(e) => setReinviteUnionOnly(e.checked === true)}
                checked={reinviteUnionOnly}
              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Union members only
                                </Checkbox.Label></Checkbox.Root>
            </HStack>
            {specificPriorId && (
              <Text color="gray.500" fontSize="xs">
                “Union members only” applies only to “Anyone with prior attendance.”
              </Text>
            )}
            {!priorSelection
              ? <Text color="gray.500" fontSize="sm" py={6} textAlign="center">Choose a prior event, or “Anyone with prior attendance,” to see recruits.</Text>
              : priorSelection === ANY_PRIOR_OPTION
                ? renderResults(anyPriorSearch.data?.items ?? [], anyPriorSearch.isLoading, 'No prior attendees found.')
                : renderResults(priorAttendees.data?.items ?? [], priorAttendees.isLoading, 'No attendees found for that event.')}
          </VStack>
        )}
      </Box>

      {openContactId && (
        <ContactDetailModal contactId={openContactId} isOpen onClose={() => setOpenContactId(null)} />
      )}

      <ContactCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        context={{ kind: 'lobbyDay', eventId }}
        onCreated={(contactId) => { void add(contactId) }}
      />
    </Box>
  );
}
